import { useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Sparkles,
  MessageSquare,
  Bell,
  Star,
  Settings,
} from "lucide-react";
import { useEvent } from "../context/EventContext";
import { useSections } from "../context/SectionsContext";
import { ICON_OPTIONS } from "../lib/constants";
import { useThemeColors } from "../hooks/useThemeColors";

export function Header({
  onMenuClick,
  showBackButton = false,
  onBackClick,
  actions = [],
  subtitle = null,
  searchProps = null,
}) {
  const location = useLocation();
  const { eventSlug, id } = useParams();
  const { event } = useEvent();
  const { sections } = useSections();
  const { themeColor } = useThemeColors();

  // Get icon component from ICON_OPTIONS by name
  const getSectionIcon = (iconName) => {
    const iconOption = ICON_OPTIONS.find((option) => option.name === iconName);
    return iconOption
      ? iconOption.icon
      : ICON_OPTIONS.find((option) => option.name === "list").icon;
  };

  // Get icon for page type
  const getPageIcon = (path) => {
    if (path.includes("/messages")) return MessageSquare;
    if (path.includes("/notifications")) return Bell;
    if (path.includes("/copilot")) return Sparkles;
    if (path.includes("/attendees")) return Users;
    if (path.includes("/favorites")) return Star;
    if (path.includes("/profile")) return Settings;
    return null;
  };

  // Get current section if we're on a section page
  const getCurrentSection = () => {
    // Extract section ID from URL path if useParams doesn't work
    let sectionId = id;
    if (!sectionId && location.pathname.includes("/sections/")) {
      const pathParts = location.pathname.split("/");
      const sectionsIndex = pathParts.findIndex((part) => part === "sections");
      if (sectionsIndex !== -1 && pathParts[sectionsIndex + 1]) {
        sectionId = pathParts[sectionsIndex + 1];
      }
    }

    if (sectionId && sections) {
      const section = sections.find(
        (section) => section.id === parseInt(sectionId)
      );
      return section;
    }
    return null;
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;

    // Check if we're on a section page
    if (path.includes("/sections/")) {
      const section = getCurrentSection();
      return section ? section.name : "Section";
    }

    if (path.includes("/login")) return "Login";
    if (path.includes("/verify")) return "Verify Code";
    if (path.includes("/profile-setup")) return "Complete Profile";
    if (path.includes("/profile")) return "Settings";
    if (path.includes("/events")) return "Events";
    if (path.includes("/messages")) return "Messages";
    if (path.includes("/notifications")) return "Notifications";
    if (path.includes("/copilot")) return "Copilot";
    if (path.includes("/attendees")) return "Attendees";
    if (path.includes("/favorites")) return "Favorites";
    if (path.includes("/schedule")) return "Schedule";
    if (path.includes("/speakers")) return "Speakers";
    if (path.includes("/sponsors")) return "Sponsors";
    if (path.includes("/map")) return "Interactive Map";
    if (path.includes("/feedback")) return "App Feedback";
    if (path.includes("/install")) return "Install App";

    return "Event App";
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      // Default back behavior - go to events page
      window.history.back();
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="px-4 py-4 sm:px-6 max-w-screen-md mx-auto">
        <div className="flex items-center justify-between min-h-[36px]">
          {/* Left side - Back button/Menu and Title */}
          <div className="flex items-center gap-3">
            {showBackButton ? (
              <button
                onClick={handleBackClick}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <></>
            )}

            {/* Page title with icon and subtitle */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                {(() => {
                  const currentSection = getCurrentSection();
                  const isSectionPage =
                    location.pathname.includes("/sections/") && currentSection;
                  const pageIcon = getPageIcon(location.pathname);

                  if (isSectionPage) {
                    const IconComponent = getSectionIcon(currentSection.icon);
                    return (
                      <>
                        <IconComponent className="h-5 w-5 text-gray-600" />
                        <div className="flex flex-col">
                          <h1 className="text-lg font-semibold text-gray-900">
                            {getPageTitle()}
                          </h1>
                        </div>
                      </>
                    );
                  }

                  if (pageIcon) {
                    const IconComponent = pageIcon;
                    return (
                      <>
                        <IconComponent className="h-5 w-5 text-gray-600" />
                        <h1 className="text-lg font-semibold text-gray-900">
                          {getPageTitle()}
                        </h1>
                      </>
                    );
                  }

                  return (
                    <h1 className="text-lg font-semibold text-gray-900">
                      {getPageTitle()}
                    </h1>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Center - Empty space */}
          <div className="flex-1"></div>
          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            {actions.map((action, index) => {
              const IconComponent = action.icon;
              return (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors relative"
                  title={action.title}
                >
                  <IconComponent className="w-5 h-5" />
                  {action.badge && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {action.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Input - shown when searchProps is provided */}
        {searchProps && (
          <div className="px-4 py-2 border-t border-gray-200">
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchProps.value}
              onChange={searchProps.onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ "--tw-ring-color": themeColor || "#3B82F6" }}
            />
          </div>
        )}
      </div>
    </header>
  );
}
